import { Component, type ErrorInfo, type ReactNode } from "react";
import { ErrorPage } from "@/features/app/pages/ErrorPage";
import { logger } from "@/lib/logging/logger";

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
};

const log = logger.child("AppErrorBoundary");

/**
 * Last-resort fallback for when the styled `ErrorPage` itself throws while
 * rendering: it shares the app's design-system chrome (`StatusPage`,
 * the creator header, theme scope), so a crash originating inside that chain
 * would re-throw from the fallback, escape the boundary, and blank the app.
 * Deliberately dependency-free — plain elements and inline styles only, with
 * an explicit background/color pair so it stays legible regardless of what
 * theme classes the crash left on the document.
 */
const BareErrorFallback = () => (
  <div
    style={{
      minHeight: "100svh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "2rem",
      textAlign: "center",
      fontFamily: "system-ui, sans-serif",
      backgroundColor: "#ffffff",
      color: "#111111",
    }}
  >
    <div>
      <h1 style={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "0.5rem" }}>Something went wrong</h1>
      <p style={{ marginBottom: "1.5rem" }}>Please reload the page, or return to the homepage.</p>
      <button
        data-analytics-ignore=""
        type="button"
        onClick={() => window.location.reload()}
        style={{
          padding: "0.5rem 1rem",
          marginRight: "0.75rem",
          border: "1px solid #d1d5db",
          borderRadius: "0.25rem",
          background: "#f9fafb",
          cursor: "pointer",
          font: "inherit",
        }}
      >
        Reload
      </button>
      <a data-analytics-ignore="" href="/" style={{ color: "#2563eb" }}>
        Go to homepage
      </a>
    </div>
  </div>
);

type ErrorPageBoundaryState = {
  fallbackFailed: boolean;
};

/**
 * Inner guard around the styled error page. An exception thrown while a
 * boundary renders its own fallback propagates past that boundary, so
 * `AppErrorBoundary` must not let `ErrorPage`'s shared dependencies take the
 * whole tree down with them.
 */
class ErrorPageBoundary extends Component<{ children: ReactNode }, ErrorPageBoundaryState> {
  state: ErrorPageBoundaryState = { fallbackFailed: false };

  static getDerivedStateFromError(): ErrorPageBoundaryState {
    return { fallbackFailed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    log.error("Error page failed to render", {
      error,
      attributes: {
        componentStack: info.componentStack,
      },
    });
  }

  render() {
    if (this.state.fallbackFailed) {
      return <BareErrorFallback />;
    }

    return this.props.children;
  }
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    hasError: false,
    error: undefined,
  };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    log.error("Unhandled application error", {
      error,
      attributes: {
        componentStack: info.componentStack,
      },
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      return (
        <ErrorPageBoundary>
          <ErrorPage error={this.state.error} onRetry={this.handleReset} />
        </ErrorPageBoundary>
      );
    }

    return this.props.children;
  }
}
